import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { speicher } from '../useUebung'
import type { Profil } from '../speicher/typen'
import { tonSchalten, tonRichtig } from '../ton'
import { Sterne } from '../spiel/Deko'
import { alleKarten } from '../karten/karten'
import { heute as heuteIso } from '../scheduler/datum'
import { FACH_INTERVALLE } from '../config'
import { BALLONSETS, type Ballonset } from '../spiel/ballonsets'
import { anzahlSicher, SICHER_AB_FACH } from '../speicher/fortschritt'
import type { Kartenstand } from '../scheduler/typen'

/**
 * Einstellungen und Elternblick.
 *
 * Die Uebersicht unten ist nicht fuer Lina gedacht, sondern fuer Chris: Sie
 * beantwortet die Frage, ob die App ueberhaupt wirkt. Genau deshalb wird die
 * Antwort-Historie vollstaendig mitgeschrieben.
 */
export function Einstellungen() {
  const [profil, setProfil] = useState<Profil | null>(null)
  const [staende, setStaende] = useState<Kartenstand[]>([])

  useEffect(() => {
    void (async () => {
      setProfil(await speicher.profilLaden())
      setStaende(await speicher.staendeLaden(heuteIso()))
    })()
  }, [])

  async function setWaehlen(id: string) {
    if (!profil) return
    const neu = { ...profil, ballonset: id }
    setProfil(neu)
    if (neu.tonAn) tonRichtig(1)
    await speicher.profilSpeichern(neu)
  }

  async function tonUmschalten() {
    if (!profil) return
    const neu = { ...profil, tonAn: !profil.tonAn }
    setProfil(neu)
    tonSchalten(neu.tonAn)
    if (neu.tonAn) tonRichtig(1)
    await speicher.profilSpeichern(neu)
  }

  const nachFach = FACH_INTERVALLE.map((_, fach) => staende.filter((s) => s.fach === fach).length)
  const sitzt = anzahlSicher(staende)
  const haengt = staende.filter((s) => s.fach <= 1 && !s.erstkontakt)
  const neu = staende.filter((s) => s.erstkontakt).length

  return (
    <div className="buehne" style={{ overflow: 'auto' }}>
      <Sterne anzahl={20} />

      <div style={{ padding: '4vmin', position: 'relative', zIndex: 1 }}>
        <h1 className="titel" style={{ fontSize: '6vmin' }}>
          Einstellungen
        </h1>

        <div className="zeile" style={{ justifyContent: 'flex-start', margin: '3vmin 0' }}>
          <button className="knopf" onClick={tonUmschalten} type="button">
            Ton: {profil?.tonAn ? 'an' : 'aus'}
          </button>
          <Link className="knopf knopf--leise" to="/tv-check">
            Fernseher-Check
          </Link>
          <Link className="knopf knopf--leise" to="/">
            Zurück
          </Link>
        </div>

        <h2 style={{ fontSize: '3.6vmin', color: 'var(--gold)', marginTop: '5vmin' }}>
          Deine Ballons
        </h2>
        <p style={{ fontSize: '2.6vmin', opacity: 0.8, maxWidth: '85vmin', lineHeight: 1.5 }}>
          Neue Farben kommen dazu, wenn mehr Aufgaben sicher sitzen. Welche du
          spielst, entscheidest du selbst.
        </p>
        <div className="setliste">
          {BALLONSETS.map((set) => (
            <Farbwelt
              key={set.id}
              set={set}
              gewaehlt={profil?.ballonset === set.id}
              offen={sitzt >= set.abSicher}
              nochKarten={set.abSicher - sitzt}
              onWaehlen={() => void setWaehlen(set.id)}
            />
          ))}
        </div>

        <h2 style={{ fontSize: '3.6vmin', color: 'var(--gold)', marginTop: '5vmin' }}>
          Wie weit ist Lina?
        </h2>

        <div className="karten" style={{ justifyContent: 'flex-start' }}>
          <div className="karte">
            <div className="karte__wert">
              {sitzt} / {alleKarten().length}
            </div>
            <div className="karte__name">sitzen sicher (Fach {SICHER_AB_FACH}+)</div>
          </div>
          <div className="karte">
            <div className="karte__wert">{haengt.length}</div>
            <div className="karte__name">hängen noch</div>
          </div>
          <div className="karte">
            <div className="karte__wert">{neu}</div>
            <div className="karte__name">noch nie drangewesen</div>
          </div>
        </div>

        <h3 style={{ fontSize: '3vmin', marginTop: '4vmin' }}>Verteilung über die Fächer</h3>
        <p style={{ fontSize: '2.4vmin', opacity: 0.75, maxWidth: '90vmin', lineHeight: 1.5 }}>
          Je höher das Fach, desto länger die Pause bis zur nächsten Wiederholung. Fach 0 heißt
          „kommt sofort wieder“, Fach 6 heißt „erst in 30 Tagen“.
        </p>
        <div style={{ display: 'flex', gap: '1.5vmin', marginTop: '2vmin', flexWrap: 'wrap' }}>
          {nachFach.map((anzahl, fach) => (
            <div key={fach} className="karte" style={{ minWidth: '14vmin', padding: '2vmin' }}>
              <div className="karte__wert" style={{ fontSize: '4.5vmin' }}>
                {anzahl}
              </div>
              <div className="karte__name">
                Fach {fach}
                <br />
                {FACH_INTERVALLE[fach] === 0 ? 'sofort' : `${FACH_INTERVALLE[fach]} Tage`}
              </div>
            </div>
          ))}
        </div>

        {haengt.length > 0 && (
          <>
            <h3 style={{ fontSize: '3vmin', marginTop: '4vmin' }}>Diese Aufgaben hängen noch</h3>
            <div className="zeile" style={{ justifyContent: 'flex-start', marginTop: '1.5vmin' }}>
              {haengt.map((s) => (
                <span
                  key={s.karte}
                  className="tag"
                  style={{ width: 'auto', padding: '0 2vmin', fontSize: '3vmin' }}
                >
                  {s.karte.replace('x', ' × ')}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Eine Farbwelt zur Auswahl.
 *
 * Gesperrte Welten werden gezeigt, nicht versteckt: Zu sehen, was es noch
 * gibt, ist der halbe Reiz. Daneben steht, wie viele Aufgaben noch fehlen,
 * damit das Ziel greifbar bleibt statt geheimnisvoll zu sein.
 */
function Farbwelt({
  set,
  gewaehlt,
  offen,
  nochKarten,
  onWaehlen,
}: {
  set: Ballonset
  gewaehlt: boolean
  offen: boolean
  nochKarten: number
  onWaehlen: () => void
}) {
  const klassen = ['set']
  if (gewaehlt) klassen.push('set--gewaehlt')
  if (!offen) klassen.push('set--zu')

  return (
    <button
      className={klassen.join(' ')}
      onClick={offen ? onWaehlen : undefined}
      disabled={!offen}
      type="button"
      aria-label={offen ? `Ballons ${set.name} wählen` : `${set.name}, noch gesperrt`}
    >
      <span className="set__farben">
        {set.farben.map((farbe) => (
          <span key={farbe} className="set__punkt" style={{ background: farbe }} />
        ))}
      </span>
      <span className="set__name">{set.name}</span>
      <span className="set__status">
        {gewaehlt ? 'ausgewählt' : offen ? 'bereit' : `noch ${nochKarten}`}
      </span>
    </button>
  )
}
